import React, { useState } from 'react';
import AdminLayout from './components/adminlayout';

const Guide = () => {
    const [openSection, setOpenSection] = useState('getting-started');

    const toggle = (id) => setOpenSection(openSection === id ? '' : id);

    const Section = ({ id, title, children, icon }) => (
        <div className='bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden'>
            <button
                onClick={() => toggle(id)}
                className='w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors'
            >
                <div className='flex items-center gap-3'>
                    <span className='text-xl'>{icon}</span>
                    <span className='font-bold text-sm'>{title}</span>
                </div>
                <span className='text-gray-400 text-lg'>{openSection === id ? '−' : '+'}</span>
            </button>
            {openSection === id && (
                <div className='px-5 pb-5 border-t border-gray-100 pt-4 text-sm text-gray-700 leading-relaxed'>
                    {children}
                </div>
            )}
        </div>
    );

    const Code = ({ children }) => (
        <code className='bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono'>{children}</code>
    );

    const CodeBlock = ({ children }) => (
        <pre className='bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto my-3 font-mono'>{children}</pre>
    );

    const Step = ({ n, children }) => (
        <div className='flex gap-3 mb-3'>
            <span className='bg-black text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0'>{n}</span>
            <div>{children}</div>
        </div>
    );

    return (
        <AdminLayout>
            <div className='p-6 max-w-4xl'>
                <div className='mb-6'>
                    <h2 className='text-xl font-bold'>Setup Guide</h2>
                    <p className='text-sm text-gray-500'>Everything you need to configure and run Coursea</p>
                </div>

                <Section id="getting-started" title="Getting Started" icon="🚀">
                    <p className='mb-3'>Coursea is an AI-powered course generation platform. Users can create courses on any topic using AI, complete with text content, YouTube videos, images, and exams.</p>
                    <p className='font-bold mb-2'>Quick Setup Checklist:</p>
                    <div className='space-y-2'>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Set up an AI provider (Gemini, OpenAI, or Claude)</label>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Configure SMTP email for notifications</label>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Create subscription plans</label>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Write legal pages (Terms, Privacy, etc.)</label>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Configure payment gateways (optional)</label>
                        <label className='flex items-center gap-2'><input type='checkbox' className='rounded' /> Set up social login (optional)</label>
                    </div>
                </Section>

                <Section id="ai-setup" title="AI Provider Setup" icon="🤖">
                    <p className='mb-3'>Go to <strong>Settings → AI Provider</strong> to configure which AI generates courses.</p>

                    <p className='font-bold mb-2'>Option A: Google Gemini (default)</p>
                    <Step n="1">Go to <strong>ai.google.dev</strong> and sign in</Step>
                    <Step n="2">Click "Get API Key" → "Create API Key"</Step>
                    <Step n="3">Copy the key and paste it in Settings → Gemini API Key</Step>
                    <Step n="4">Recommended model: <Code>gemini-1.5-flash</Code> (fast) or <Code>gemini-pro</Code> (quality)</Step>

                    <p className='font-bold mb-2 mt-4'>Option B: OpenAI (GPT)</p>
                    <Step n="1">Go to <strong>platform.openai.com/api-keys</strong></Step>
                    <Step n="2">Create a new API key</Step>
                    <Step n="3">Paste in Settings → OpenAI API Key</Step>
                    <Step n="4">Recommended model: <Code>gpt-4o-mini</Code> (cheap) or <Code>gpt-4o</Code> (quality)</Step>

                    <p className='font-bold mb-2 mt-4'>Option C: Claude (Anthropic)</p>
                    <Step n="1">Go to <strong>console.anthropic.com</strong></Step>
                    <Step n="2">Create an API key</Step>
                    <Step n="3">Paste in Settings → Claude API Key</Step>
                    <Step n="4">Recommended model: <Code>claude-sonnet-4-20250514</Code></Step>
                </Section>

                <Section id="railway-env" title="Railway Environment Variables" icon="🔧">
                    <p className='mb-3'>For <strong>security</strong>, sensitive keys (payment secrets, database credentials) should be stored in Railway environment variables, not in the admin panel.</p>

                    <p className='font-bold mb-2'>How to add variables in Railway:</p>
                    <Step n="1">Go to <strong>railway.app</strong> → your project → click on the service</Step>
                    <Step n="2">Click the <strong>Variables</strong> tab</Step>
                    <Step n="3">Click <strong>"Raw Editor"</strong> for bulk editing, or <strong>"+ New Variable"</strong> for individual ones</Step>
                    <Step n="4">Add your variables and click <strong>"Update Variables"</strong></Step>
                    <p className='mb-3'>Railway will automatically redeploy after saving.</p>

                    <p className='font-bold mb-2'>Required variables:</p>
                    <CodeBlock>{`MONGODB_URI=mongodb+srv://user:pass@cluster.mongodb.net/db
API_KEY=your_ai_api_key
EMAIL=your@email.com
PASSWORD=smtp_app_password
UNSPLASH_ACCESS_KEY=your_unsplash_key`}</CodeBlock>

                    <p className='font-bold mb-2'>Payment variables (if using payments):</p>
                    <CodeBlock>{`STRIPE_SECRET_KEY=sk_live_...
PAYPAL_CLIENT_ID=...
PAYPAL_APP_SECRET_KEY=...
RAZORPAY_KEY_ID=...
RAZORPAY_KEY_SECRET=...
PAYSTACK_SECRET_KEY=...
FLUTTERWAVE_PUBLIC_KEY=...
FLUTTERWAVE_SECRET_KEY=...`}</CodeBlock>

                    <p className='font-bold mb-2'>Plan config variables:</p>
                    <CodeBlock>{`MONTH_TYPE=Monthly Plan
MONTH_COST=9
YEAR_TYPE=Yearly Plan
YEAR_COST=99
WEBSITE_URL=https://yourdomain.com`}</CodeBlock>
                </Section>

                <Section id="plans" title="Setting Up Plans" icon="💰">
                    <p className='mb-3'>Go to <strong>Plans</strong> in the sidebar to create subscription plans.</p>
                    <Step n="1">Click <strong>"+ Add Plan"</strong></Step>
                    <Step n="2">Fill in name (e.g. "Free Plan"), price, duration</Step>
                    <Step n="3">Add features (one per line) that will display on the pricing page</Step>
                    <Step n="4">Set the order (0 = first, 1 = second, etc.)</Step>
                    <Step n="5">Click <strong>Save</strong></Step>

                    <p className='mt-3 mb-2'>Typical setup:</p>
                    <div className='grid grid-cols-3 gap-3'>
                        <div className='border rounded-lg p-3 text-center'>
                            <p className='font-bold'>Free</p>
                            <p className='text-2xl font-black'>$0</p>
                            <p className='text-xs text-gray-500'>Limited features</p>
                        </div>
                        <div className='border-2 border-black rounded-lg p-3 text-center'>
                            <p className='font-bold'>Monthly</p>
                            <p className='text-2xl font-black'>$9</p>
                            <p className='text-xs text-gray-500'>Full access</p>
                        </div>
                        <div className='border rounded-lg p-3 text-center'>
                            <p className='font-bold'>Yearly</p>
                            <p className='text-2xl font-black'>$99</p>
                            <p className='text-xs text-gray-500'>Best value</p>
                        </div>
                    </div>
                </Section>

                <Section id="email" title="Email / SMTP Setup" icon="📧">
                    <p className='mb-3'>Email is used for password resets and subscription notifications.</p>

                    <p className='font-bold mb-2'>Gmail Setup:</p>
                    <Step n="1">Enable 2-Step Verification on your Google account</Step>
                    <Step n="2">Go to <strong>myaccount.google.com → Security → 2-Step Verification → App Passwords</strong></Step>
                    <Step n="3">Generate a new app password for "Mail"</Step>
                    <Step n="4">In Settings, set: Host = <Code>smtp.gmail.com</Code>, Port = <Code>465</Code></Step>
                    <Step n="5">Email = your Gmail, Password = the app password (NOT your Gmail password)</Step>
                </Section>

                <Section id="users" title="Managing Users" icon="👥">
                    <p className='mb-3'>Go to <strong>Users</strong> to manage all registered users.</p>

                    <p className='font-bold mb-2'>What you can do:</p>
                    <ul className='list-disc pl-5 space-y-1'>
                        <li><strong>Search</strong> — find users by name or email</li>
                        <li><strong>Edit</strong> — change user name or email</li>
                        <li><strong>Change subscription</strong> — use the dropdown to assign Free, Monthly, Yearly, or Forever (admin)</li>
                        <li><strong>Reset password</strong> — set a new password for any user</li>
                        <li><strong>Delete</strong> — remove user and all their courses</li>
                    </ul>

                    <p className='font-bold mb-2 mt-4'>User types:</p>
                    <ul className='list-disc pl-5 space-y-1'>
                        <li><Code>free</Code> — free tier, limited features</li>
                        <li><Code>Monthly Plan</Code> — active monthly subscription</li>
                        <li><Code>Yearly Plan</Code> — active yearly subscription</li>
                        <li><Code>forever</Code> — admin/lifetime access</li>
                    </ul>
                </Section>

                <Section id="admin-flow" title="How the Platform Works" icon="📋">
                    <p className='font-bold mb-2'>User Flow:</p>
                    <Step n="1">User signs up (email/password or Google/Facebook)</Step>
                    <Step n="2">User goes to "Generate Course" → enters a topic</Step>
                    <Step n="3">AI generates course structure (subtopics)</Step>
                    <Step n="4">User selects subtopics → AI generates content for each</Step>
                    <Step n="5">Course is saved with text, images, and YouTube videos</Step>
                    <Step n="6">User can take exams, download certificates, share courses</Step>

                    <p className='font-bold mb-2 mt-4'>Admin Flow:</p>
                    <Step n="1">First user to sign up becomes the main admin</Step>
                    <Step n="2">Admin accesses panel via the "Admin" button in navigation</Step>
                    <Step n="3">Admin manages users, plans, settings, and legal pages</Step>
                    <Step n="4">Additional admins can be added from the Admins page</Step>
                </Section>

                <Section id="domain" title="Custom Domain Setup" icon="🌐">
                    <p className='mb-3'>To connect your custom domain (e.g. coursea.co) to Railway:</p>
                    <Step n="1">In Railway → your service → <strong>Settings → Networking</strong></Step>
                    <Step n="2">Click <strong>"+ Custom Domain"</strong> and enter your domain</Step>
                    <Step n="3">Railway will give you a <strong>CNAME record</strong></Step>
                    <Step n="4">Go to your domain registrar (Namecheap, GoDaddy, Cloudflare, etc.)</Step>
                    <Step n="5">Add a <strong>CNAME record</strong> pointing to the Railway value</Step>
                    <Step n="6">Wait for DNS propagation (5-30 minutes)</Step>
                    <Step n="7">Railway will automatically issue an SSL certificate</Step>
                </Section>

                <Section id="security" title="Security Best Practices" icon="🔒">
                    <ul className='list-disc pl-5 space-y-2'>
                        <li>Store payment secret keys in <strong>Railway environment variables</strong>, not in the admin panel</li>
                        <li>Use <strong>App Passwords</strong> for Gmail SMTP, never your actual password</li>
                        <li>AI API keys can be stored in Settings (database) or Railway env vars — both work</li>
                        <li>The <Code>MONGODB_URI</Code> should always be in Railway env vars</li>
                        <li>Admin endpoints are protected — only users in the Admins list can access them</li>
                        <li>Passwords are hidden from all API responses</li>
                        <li>Regularly review the Users list for suspicious accounts</li>
                    </ul>
                </Section>
            </div>
        </AdminLayout>
    );
};
export default Guide;
